import React from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { PartyPopper, Mail, Wallet, ArrowRight } from "lucide-react";

// Fixed, deterministic confetti layout so it doesn't reshuffle on re-render.
// Pure CSS — no animation library needed.
const CONFETTI = [
  { left: "6%", delay: "0s", color: "#f97316", size: 8 },
  { left: "16%", delay: "0.15s", color: "#22c55e", size: 6 },
  { left: "27%", delay: "0.05s", color: "#3b82f6", size: 7 },
  { left: "38%", delay: "0.3s", color: "#f97316", size: 6 },
  { left: "49%", delay: "0.1s", color: "#eab308", size: 8 },
  { left: "60%", delay: "0.25s", color: "#22c55e", size: 6 },
  { left: "71%", delay: "0s", color: "#3b82f6", size: 7 },
  { left: "82%", delay: "0.2s", color: "#f97316", size: 6 },
  { left: "92%", delay: "0.1s", color: "#eab308", size: 7 },
];

const RegistrationSuccess = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  // If someone navigates here manually, redirect them home
  if (!state) {
    return <Navigate to="/" replace />;
  }

  const { teamName, totalCost } = state;

  return (
    <div className="max-w-2xl mx-auto py-12 sm:py-16">
      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(140px) rotate(340deg); opacity: 0; }
        }
        .confetti-piece {
          animation: confettiFall 1.4s ease-in forwards;
        }
      `}</style>

      <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 p-8 sm:p-10 text-center">
        {/* Confetti burst, contained to the top of the card */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 overflow-hidden">
          {CONFETTI.map((c, i) => (
            <span
              key={i}
              className="confetti-piece absolute top-0 rounded-sm"
              style={{
                left: c.left,
                width: c.size,
                height: c.size,
                backgroundColor: c.color,
                animationDelay: c.delay,
              }}
            />
          ))}
        </div>

        <div className="relative mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg shadow-orange-500/30">
          <PartyPopper className="text-white" size={28} />
        </div>

        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white mb-2">
          You're in!
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mb-8">
          <span className="font-semibold text-gray-900 dark:text-white">
            {teamName}
          </span>{" "}
          is officially registered for the upcoming season.
        </p>

        <div className="text-left space-y-3 mb-8">
          <div className="flex items-start gap-3 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400">
              <Wallet size={16} />
            </span>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">
                Total due: ${totalCost}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Payable via Zelle, Venmo, or Cash App — details are in your
                email.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
              <Mail size={16} />
            </span>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">
                Check your inbox
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                A confirmation email with payment instructions is on its way.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate("/")}
          className="group inline-flex items-center gap-2 bg-orange-600 dark:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold text-lg hover:bg-orange-700 dark:hover:bg-orange-600 hover:shadow-lg hover:shadow-orange-500/20 transition-all"
        >
          Return Home
          <ArrowRight
            size={18}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </button>
      </div>
    </div>
  );
};

export default RegistrationSuccess;
